import { Chip, Stack } from '@mui/material';

import { EsgRecord } from '../../../types/syntiro_honey';
import { ReviewFilters } from './useRecordList';
import { RecordStats } from './useRecordStats';

interface ReviewSummaryChipsProps {
  /** The triage counts, or null while the first load is in flight. */
  stats: RecordStats | null;
  /** The committed filters (drives which chip renders as active). */
  filters: ReviewFilters;
  /** Called with the full next filter set when a chip is toggled. */
  onFiltersChange: (filters: ReviewFilters) => void;
}

/** Action-oriented triage buckets: done / confirm / fix, by mapping status. */
const BUCKETS: Array<{
  status: EsgRecord['mappingStatus'];
  key: keyof RecordStats;
  label: string;
  color: 'success' | 'warning' | 'default';
}> = [
  { status: 'EXACT', key: 'exact', label: 'Exact', color: 'success' },
  { status: 'SUGGESTED', key: 'suggested', label: 'To confirm', color: 'warning' },
  { status: 'UNMAPPED', key: 'unmapped', label: 'Unmapped', color: 'default' }
];

/**
 * At-a-glance triage chips above the records grid: counts by MAPPING status
 * (Exact / To confirm / Unmapped) so a reviewer sees, and can jump to, the work
 * that needs doing — plus a Learned chip counting the records the alias loop
 * mapped from the team's own prior confirmations (a subset of Exact; the loop's
 * payoff, #43). Clicking a chip applies that filter; clicking the active one
 * clears it.
 * @returns {JSX.Element | null} The chips, or null until counts are loaded.
 */
export default function ReviewSummaryChips({
  stats,
  filters,
  onFiltersChange
}: ReviewSummaryChipsProps) {
  if (stats === null) {
    return null;
  }
  const learnedActive = filters.mappedFromAlias === true;
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      {BUCKETS.map((bucket) => {
        const active = filters.mappingStatus === bucket.status;
        return (
          <Chip
            key={bucket.status}
            label={`${bucket.label} ${stats[bucket.key]}`}
            size="small"
            color={bucket.color}
            variant={active ? 'filled' : 'outlined'}
            onClick={() =>
              onFiltersChange({
                ...filters,
                mappingStatus: active ? undefined : bucket.status
              })
            }
          />
        );
      })}
      <Chip
        label={`Learned ${stats.learned}`}
        size="small"
        color="info"
        variant={learnedActive ? 'filled' : 'outlined'}
        onClick={() =>
          onFiltersChange({
            ...filters,
            mappedFromAlias: learnedActive ? undefined : true
          })
        }
      />
    </Stack>
  );
}
